import React from 'react'

import spacetime from 'spacetime'

import {
  Flex,
  Header,
  Paper,
  Table,
  Text,
  UserAvatar,
  useTableState,
} from '@driftt/dds'

import useMeetingsBookedByMe, {
  MeetingBookedByMe,
} from 'hooks/useMeetingsBookedByMe'

import MeetingAttendeesCell from './MeetingAttendeesCell'
import MeetingOutcomeCell from './MeetingOutcomeCell'

const SOURCE_HUMANIZED_TRANSLATION = {
  CALENDAR_DROP: 'Calendar share',
  EMAIL_DROP: 'Email extension',
  PLAYBOOK_DROP: 'Playbook',
  PROFILE_DROP: 'Drift Profile',
  PROFILE_LINK: 'Drift Profile',
  RESCHEDULE: 'Reschedule',
  SEQUENCE_DROP: 'Sequence email',
  NONE: 'None',
  FALLBACK_EMAIL: 'Fallback email',
  VIDEO_MEETING: 'Video meeting',
  PLAYBOOK_DROP_TEAM: 'Playbook',
  SITE_CONCIERGE: 'Site Concierge',
}

export const MeetingsBookedTable = () => {
  const { tableState, handleTableStateChange } =
    useTableState<MeetingBookedByMe>({
      defaultPagination: {
        pageIndex: 0,
        pageSize: 5,
      },
    })

  const { meetingsData, isLoading, updateStatus } = useMeetingsBookedByMe(
    tableState.pagination.pageIndex,
    tableState.pagination.pageSize
  )

  if ((!isLoading && !meetingsData) || meetingsData?.totalRowCount === 0) {
    return null
  } else {
    return (
      <Paper
        elevation={2}
        css={{
          marginBottom: '$4',
        }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          css={{ padding: '$3 $2 $2' }}
        >
          <Header variant="h2">Meetings booked by me</Header>
        </Flex>
        <Flex direction="column" fullWidth gap={2}>
          <Table<MeetingBookedByMe>
            isLoading={isLoading}
            loadingRows={5}
            disableBoxShadow
            columns={[
              {
                header: 'Prospect',
                value: 'endUserName',
                cell: ({ endUserName, endUserAvatar }) => (
                  <Flex alignItems="center">
                    <UserAvatar
                      size="small"
                      name={endUserName}
                      imageUrl={endUserAvatar}
                    />
                    <Text isPrivate>{endUserName}</Text>
                  </Flex>
                ),
              },
              {
                value: 'companyName',
                header: 'Company',
              },
              {
                value: 'bookedTeammateNames',
                header: 'Booked teammates',
                cell: ({ bookedTeammateNames }) => (
                  <MeetingAttendeesCell
                    participantNames={bookedTeammateNames}
                  />
                ),
              },
              {
                value: 'startTime',
                header: 'Meeting date',
                cell: ({ startTime }) =>
                  spacetime(startTime).format('{mm/dd/yyyy}'),
              },
              {
                value: 'meetingSource',
                header: 'Source',
                cell: ({ meetingSource }) =>
                  SOURCE_HUMANIZED_TRANSLATION[
                    meetingSource as keyof typeof SOURCE_HUMANIZED_TRANSLATION
                  ],
              },
              {
                value: 'status',
                header: 'Meeting status',
                cell: ({ status, eventId, endTime }) => (
                  <MeetingOutcomeCell
                    status={status}
                    eventId={eventId}
                    meetingEnd={endTime}
                    updateStatus={updateStatus}
                  />
                ),
              },
            ]}
            rows={meetingsData?.rows ?? []}
            sorting={{ defaultSort: [{ id: 'startTime', desc: true }] }}
            pagination={{
              rowTotal: meetingsData?.totalRowCount ?? 0,
              defaultPagination: tableState.pagination,
            }}
            onStateChange={handleTableStateChange}
          />
        </Flex>
      </Paper>
    )
  }
}
